import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Drawer,
  Form,
  Input,
  Space,
  Popconfirm,
  message,
  Pagination,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { FiSearch } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import pricingGroupService from '../../services/pricingGroupService';

const { TextArea } = Input;

export default function PricingGroupsPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_GROUPS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.PRICING_GROUPS_EDIT);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [form] = Form.useForm();

  const isEdit = Boolean(editing);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const list = await pricingGroupService.list();
      setGroups(list);
    } catch (e) {
      message.error(e?.message || 'Failed to load pricing groups');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const q = searchInput.trim().toLowerCase();
  const filtered = !q
    ? groups
    : groups.filter((g) => {
        const a = (g.group_name || '').toLowerCase();
        const b = (g.description || '').toLowerCase();
        return a.includes(q) || b.includes(q);
      });

  const pagedStart = (page - 1) * pageSize;
  const paged = filtered.slice(pagedStart, pagedStart + pageSize);

  useEffect(() => {
    setPage(1);
  }, [searchInput]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      group_name: record.group_name,
      description: record.description,
    });
    setDrawerOpen(true);
  };

  const onDrawerClose = () => {
    setDrawerOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        group_name: (values.group_name || '').trim(),
        description: values.description != null ? String(values.description).trim() : '',
      };
      if (isEdit) {
        const res = await pricingGroupService.update(editing.id, {
          group_name: payload.group_name,
          description: payload.description,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Update failed');
          return;
        }
        message.success('Pricing group updated');
      } else {
        const res = await pricingGroupService.create({
          group_name: payload.group_name,
          description: payload.description || null,
        });
        if (res?.success === false) {
          message.error(res?.message || 'Create failed');
          return;
        }
        message.success('Pricing group created');
      }
      onDrawerClose();
      loadGroups();
    } catch (e) {
      if (e?.errorFields) return;
      const msg = e?.response?.data?.message || e?.message;
      message.error(msg || (isEdit ? 'Update failed' : 'Create failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (record) => {
    try {
      const res = await pricingGroupService.remove(record.id);
      if (res?.success === false) {
        message.error(res?.message || 'Delete failed');
        return;
      }
      message.success('Pricing group deleted');
      loadGroups();
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || 'Delete failed');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'group_name',
      key: 'group_name',
      render: (text) => <span className="font-semibold text-gray-800">{text || '—'}</span>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => <span className="text-gray-600 text-sm">{text || '—'}</span>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space size="small">
          {canEdit && (
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            >
              Edit
            </Button>
          )}
          {canEdit && (
            <Popconfirm
              title="Delete this pricing group?"
              description="This is a soft delete; the row will be hidden from lists."
              onConfirm={() => onDelete(record)}
              okText="Delete"
              okButtonProps={{ danger: true }}
            >
              <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <DashboardPageWrapper>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pricing groups</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage groups such as Altcase, Partner, Gold, and Silver for pricing rules.
          </p>
        </div>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New group
          </Button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center gap-3 bg-gray-50/50">
          <div className="relative w-full sm:max-w-md">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or description"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full h-[38px] pl-9 pr-4 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all bg-white"
            />
          </div>
          <p className="text-xs text-gray-500 sm:ml-auto">
            {filtered.length} of {groups.length} groups
            {searchInput.trim() ? ' (filtered)' : ''}
          </p>
        </div>

        <div className="overflow-x-auto">
          <Table
            columns={columns}
            dataSource={paged}
            rowKey={(r) => r.id}
            loading={loading}
            pagination={false}
            className="min-w-[640px]"
          />
        </div>

        <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100">
          <Pagination
            current={page}
            pageSize={pageSize}
            total={filtered.length}
            showSizeChanger
            pageSizeOptions={['10', '15', '30', '50']}
            onChange={(p, ps) => {
              setPage(p);
              if (ps !== pageSize) {
                setPageSize(ps);
                setPage(1);
              }
            }}
            showTotal={(total, range) => (
              <span className="text-xs text-gray-500">
                {range[0]}–{range[1]} of {total}
              </span>
            )}
          />
        </div>
      </div>

      <Drawer
        title={isEdit ? 'Edit pricing group' : 'New pricing group'}
        width={400}
        open={drawerOpen}
        onClose={onDrawerClose}
        extra={
          <Space>
            <Button onClick={onDrawerClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={onSubmit}>
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="group_name"
            label="Group name"
            rules={[{ required: true, message: 'Enter a group name' }, { min: 2, message: 'At least 2 characters' }]}
          >
            <Input placeholder="e.g. Gold, Silver, Partner" maxLength={255} showCount />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <TextArea rows={4} placeholder="Optional" maxLength={5000} showCount />
          </Form.Item>
        </Form>
      </Drawer>
    </DashboardPageWrapper>
  );
}
